/**
 * 報酬設定一覧画面 (v3.2.5 / T5)。
 *
 * - 全 commission_sets を一覧表示
 * - 「新規セット作成」モーダルから新規追加
 * - 各セットには「編集」「削除」「カテゴリ割当」リンク
 * - product_categories 側に有効化中のカテゴリ数をバッジ表示
 */
import { Coins, Pencil, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import {
  useCommissionSets,
  useDeleteCommissionSet,
  useUpsertCommissionSet,
  type CommissionSetRecord,
} from '@/hooks/useCommissions';
import { useProductCatalogs } from '@/hooks/useProductCatalogs';
import { paths } from '@/routes/paths';

export function CommissionsListPage() {
  const sets = useCommissionSets();
  const catalogs = useProductCatalogs();
  const upsert = useUpsertCommissionSet();
  const del = useDeleteCommissionSet();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<CommissionSetRecord | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // v3.2.23+ 季節別の commissionSet 紐付けを表示するためのインデックス
  const catalogsByCommissionSetId = useMemo(() => {
    const map = new Map<
      string,
      Array<{ id: string; category: string; version: string; label: string; season: 'normal' | 'sale' }>
    >();
    for (const c of catalogs.data ?? []) {
      const setId = c.linkedCommissionSetId ?? null;
      if (!setId) continue;
      const list = map.get(setId) ?? [];
      list.push({
        id: c.id,
        category: c.category,
        version: c.version,
        label: c.label,
        season: c.season === 'sale' ? 'sale' : 'normal',
      });
      map.set(setId, list);
    }
    return map;
  }, [catalogs.data]);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setDescription('');
    setCreateOpen(true);
  };
  const openEdit = (set: CommissionSetRecord) => {
    setEditing(set);
    setName(set.name);
    setDescription(set.description ?? '');
    setCreateOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('セット名は必須です');
      return;
    }
    try {
      const r = await upsert.mutateAsync({
        setId: editing?.id,
        name: name.trim(),
        description: description.trim() || null,
      });
      toast.success(r.created ? '報酬セットを作成しました' : '報酬セットを更新しました');
      setCreateOpen(false);
    } catch (err) {
      toast.error(`失敗: ${(err as Error).message}`);
    }
  };

  const handleDelete = async (set: CommissionSetRecord) => {
    if (!confirm(`「${set.name}」を削除します。よろしいですか？\n※ どこかの商品カタログから紐付けられている場合は、先にそのカタログの紐付けを外してください。`)) return;
    try {
      await del.mutateAsync({ setId: set.id });
      toast.success('削除しました');
    } catch (err) {
      toast.error(`失敗: ${(err as Error).message}`);
    }
  };

  return (
    <Screen
      title="報酬設定"
      description="紹介者・SA・社員への報酬計算ルール。商品カタログから紐付け (linkedCommissionSetId) されたセットが effective として自動適用されます。"
      actions={
        <Button variant="primary" onClick={openCreate}>
          <Plus className="size-3.5" />
          新規セット作成
        </Button>
      }
    >
      {sets.isLoading ? (
        <Spinner />
      ) : !sets.data || sets.data.length === 0 ? (
        <EmptyState
          icon={Coins}
          title="報酬セットがありません"
          description="「新規セット作成」から最初のセットを作成してください。"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {sets.data.map((set) => {
            const linkedBy = catalogsByCommissionSetId.get(set.id) ?? [];
            return (
            <div
              key={set.id}
              className="rounded-md border p-4 space-y-2 border-[var(--border)] bg-[var(--bg-surface)]"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <Link
                    to={paths.commissionSetCategories.replace(':setId', set.id)}
                    className="text-sm font-semibold text-[var(--text-primary)] hover:text-[var(--accent)] truncate inline-flex items-center gap-1.5"
                  >
                    {set.name}
                  </Link>
                  {set.description && (
                    <p className="text-xs text-[var(--text-muted)] mt-0.5 line-clamp-2">
                      {set.description}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => openEdit(set)}
                    className="p-1.5 rounded text-[var(--text-muted)] hover:bg-[var(--bg-hover)]"
                    title="名前/説明を編集"
                  >
                    <Pencil className="size-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(set)}
                    className="p-1.5 rounded text-[var(--text-muted)] hover:text-[var(--accent-red)] hover:bg-[var(--bg-hover)]"
                    title="削除"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
              </div>

              {/* v3.2.23+: このセットを紐付けているカタログを表示 (effective 状態の可視化) */}
              {linkedBy.length > 0 ? (
                <div className="rounded-md border border-dashed border-[var(--border)] bg-[var(--bg-elevated)] px-2 py-1.5 text-[11px]">
                  <div className="text-[var(--text-muted)] mb-0.5">
                    紐付け中のカタログ:
                  </div>
                  <ul className="space-y-0.5">
                    {linkedBy.map((cat) => (
                      <li key={cat.id} className="flex items-center gap-1.5">
                        <span
                          className={`px-1 rounded text-[9px] ${
                            cat.season === 'sale'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {cat.season === 'sale' ? 'セール' : '通常'}
                        </span>
                        <span className="text-[var(--text-secondary)] truncate">
                          {cat.category} / {cat.label || cat.version}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="rounded-md border border-dashed border-[var(--border)] bg-[var(--bg-elevated)] px-2 py-1.5 text-[10px] text-[var(--text-muted)]">
                  どの商品カタログからも紐付けされていません (effective にならず、報酬計算には使われません)
                </div>
              )}

              <Link
                to={paths.commissionSetCategories.replace(':setId', set.id)}
                className="inline-block text-xs text-[var(--accent)] hover:underline"
              >
                リスト編集 →
              </Link>
            </div>
            );
          })}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? '報酬セットを編集' : '報酬セットを作成'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">セット名 *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="例: 2026 標準セット"
              />
            </div>
            <div>
              <Label className="text-xs">説明 (任意)</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                placeholder="例: 全カテゴリ共通。レート 10% / 紹介者 5%"
              />
            </div>
            <div className="rounded-md border border-dashed border-[var(--border)] bg-[var(--bg-elevated)] p-2 text-[10px] text-[var(--text-muted)]">
              ℹ️ 季節モードはこのセット側では設定しません。商品カタログの編集画面で
              「季節モード」 + 「紐付け 報酬セット」を指定すると、effective として自動適用されます。
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>
              キャンセル
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={upsert.isPending || !name.trim()}
            >
              {upsert.isPending ? '保存中…' : editing ? '更新' : '作成'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </Screen>
  );
}
